import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { api } from "@/lib/api";
import { PUSH_CATEGORIES, SAFETY_SUBS, pushStatus } from "@/lib/push";

const CATS = Object.keys(PUSH_CATEGORIES).filter((c) => c !== "bundle");

function PickSelect({ id, label, value, onChange, options, testId }) {
  return (
    <div><Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-2" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(options).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`${testId}-option-${k}`}>{l}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

function Result({ r }) {
  const st = pushStatus(r.status);
  return (
    <div className="mt-5 rounded-xl bg-[var(--cw-surface-tertiary)] p-4 text-sm" role="status" data-testid="test-push-result">
      <Pill tone={st.tone} data-testid="test-push-result-status">{st.label}</Pill>
      <p className="mt-3 font-medium">{r.title}</p>
      <p className="cw-soft" data-testid="test-push-result-body">{r.body || "—"}</p>
      <p className="cw-muted mt-2 text-xs">{[r.url, r.error, r.release_at && `releases ${new Date(r.release_at).toLocaleString()}`].filter(Boolean).join(" · ")}</p>
    </div>
  );
}

export default function TestPushForm({ initialUser = "", onSent }) {
  const [f, setF] = useState({ user_id: initialUser, category: "wink", sub: "verification_approved", bypass_rules: false, reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const valid = f.user_id.trim() && f.reason.trim().length >= 3;

  const send = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setResult(null);
    try {
      const body = { user_id: f.user_id.trim(), category: f.category, bypass_rules: f.bypass_rules, reason: f.reason.trim(), ...(f.category === "safety" ? { sub: f.sub } : {}) };
      setResult(await api("/admin/push/test", { body }));
      onSent?.();
    } catch (err) {
      setError(err.status === 404 ? "No user with that ID." : err.message);
    } finally { setBusy(false); }
  };

  return (
    <Panel className="p-6" data-testid="test-push-panel">
      <h2 className="font-semibold">Send a test push</h2>
      <p className="cw-soft mt-1 text-sm leading-6">Goes through the same send path as the app, with live copy. It’s logged here and in the admin log.</p>
      <form onSubmit={send} className="mt-5 space-y-4" data-testid="test-push-form">
        <div><Label htmlFor="tp-user">User ID</Label>
          <Input id="tp-user" value={f.user_id} onChange={set("user_id")} placeholder="6acf99e3…" className="mt-2 font-mono text-sm" data-testid="test-push-user-input" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <PickSelect id="tp-cat" label="Category" value={f.category} onChange={set("category")} options={Object.fromEntries(CATS.map((c) => [c, PUSH_CATEGORIES[c]]))} testId="test-push-category-select" />
          {f.category === "safety" && <PickSelect id="tp-sub" label="Safety message" value={f.sub} onChange={set("sub")} options={SAFETY_SUBS} testId="test-push-sub-select" />}
        </div>
        <label htmlFor="tp-bypass" className="flex cursor-pointer items-start gap-3 rounded-xl bg-[var(--cw-surface-tertiary)] p-4">
          <Checkbox id="tp-bypass" checked={f.bypass_rules} onCheckedChange={(v) => set("bypass_rules")(!!v)} className="mt-0.5" data-testid="test-push-bypass-checkbox" />
          <span><span className="block text-sm font-medium">Skip the rules</span>
            <span className="cw-soft block text-xs leading-5">Ignores kill switches, the person’s switches, quiet hours, the daily cap and collapsing.</span></span>
        </label>
        <div><Label htmlFor="tp-reason">Reason</Label>
          <Textarea id="tp-reason" value={f.reason} onChange={set("reason")} rows={2} className="mt-2" data-testid="test-push-reason-input" /></div>
        {error && <p className="cw-error text-sm" role="alert" data-testid="test-push-error">{error}</p>}
        <Button type="submit" disabled={!valid || busy} data-testid="test-push-send-button">{busy ? "Sending…" : "Send test push"}</Button>
      </form>
      {result && <Result r={result} />}
    </Panel>
  );
}
